/**
 * Repair-proposal generator (client-side, deterministic).
 *
 * Maps each Coverage Audit `IssueRow` to a concrete repair the auditor
 * could apply to the medical note. v1 is read-only — proposals render
 * inline in IssueCard so the auditor sees what *would* happen. v2 (a
 * follow-up) wires a `POST /repair` endpoint that executes the patch
 * against `medical_notes.artifact_content` + records audit trail.
 *
 * Why client-side first:
 *   - The signal already carries every input the proposal needs
 *     (rxnormSuggestions, noteDrift kind, target sections, evidence).
 *   - Zero backend risk while we validate the proposal text.
 *   - Easy to iterate on phrasing without redeploys.
 *
 * Proposal kinds:
 *   - 'replace_text'       — substitute one substring with another
 *                            (e.g. misspelled drug → RxNorm canonical).
 *   - 'set_section_content'— overwrite a section's content (e.g. revert
 *                            note section to summary content).
 *   - 'clear_section'      — empty a hallucinated section.
 *   - 'add_to_section'     — append content (e.g. a missing drug from
 *                            summary that didn't make it to the note).
 *   - 'manual'             — auditor input required (e.g. add a dose
 *                            value the system cannot infer).
 *   - 'unsupported'        — issue family not yet covered by a repair
 *                            template; render a clear "no auto-fix" hint.
 */
import type {
  EvidenceSpan,
  IssueRow,
} from '../../stores/coverageAuditStore';
import type { ClinicalSummary } from '../../types/clinicalSummary';
import type { MedicalNote } from '../../types/medicalNote';

export type RepairKind =
  | 'replace_text'
  | 'set_section_content'
  | 'clear_section'
  | 'add_to_section'
  | 'manual'
  | 'unsupported';

export interface RepairProposal {
  kind: RepairKind;
  // Side that would be patched. v1 always 'note' — the medical note is
  // the artifact that ships to the EHR; the summary is upstream input.
  targetSide: 'note';
  // Section key (chiefComplaint, plan, ...) when the patch is scoped to
  // one section. Null for proposals that affect the document globally
  // (none today, but reserved).
  targetSection: string | null;
  // Headline rendered on the IssueCard. Short, action-verb-led.
  headline: string;
  // Optional one-line rationale for *why* this is the suggested fix.
  rationale?: string;
  // Diff display fields. `current` is what the note has today,
  // `proposed` is what it would become. Either may be empty.
  current?: string;
  proposed?: string;
  // For 'replace_text' specifically — the substring being swapped.
  replaceFrom?: string;
  replaceTo?: string;
  // Whether v1 can auto-apply. Manual / unsupported kinds render the
  // proposal but no action button.
  autoApplicable: boolean;
}

interface BuilderInput {
  issue: IssueRow;
  summary?: ClinicalSummary | null;
  note?: MedicalNote | null;
}

function getSectionContent(
  source: Record<string, { content?: string } | undefined> | null | undefined,
  key: string,
): string {
  return (source?.[key]?.content ?? '').trim();
}

/**
 * Resolve the most specific section the issue refers to. The pipeline
 * persists `targetSections` on signals but only `evidenceBundle` on
 * issues; we recover the section from the first evidence span that
 * names one.
 */
function inferSection(issue: IssueRow): string | null {
  for (const span of issue.evidenceBundle ?? []) {
    if (span.section) return String(span.section);
  }
  return null;
}

export function getRepairProposal(input: BuilderInput): RepairProposal | null {
  const { issue, summary, note } = input;
  // Prefer the server-derived proposal whenever the listing endpoint
  // populated it. The server has access to signal-level attributes
  // (rxnormSuggestions, noteDrift, kind) that are NOT shipped to the FE
  // on the issue row, so it's the only place that can correctly emit
  // auto-applicable proposals for our detector-tagged issues.
  if (issue.proposal) {
    return issue.proposal as RepairProposal;
  }
  const attrs = (issue as IssueRow & { attributes?: Record<string, unknown> }).attributes ?? {};
  const section = inferSection(issue);

  // ------------------------------------------------------------
  // Medication: misspelling → RxNorm canonical
  // ------------------------------------------------------------
  if (
    issue.family === 'incomplete_medication_detail' &&
    attrs.rxnormVerified === false &&
    Array.isArray(attrs.rxnormSuggestions) &&
    (attrs.rxnormSuggestions as string[]).length > 0
  ) {
    const drug = String(attrs.medication ?? '');
    const suggestion = (attrs.rxnormSuggestions as string[])[0];
    return {
      kind: 'replace_text',
      targetSide: 'note',
      targetSection: section,
      headline: `Replace "${drug}" with "${suggestion}"`,
      rationale: `RxNorm has no exact match for "${drug}". Closest canonical drug name is "${suggestion}" (RxCUI ${attrs.rxnormCui ?? '—'}).`,
      current: drug,
      proposed: suggestion,
      replaceFrom: drug,
      replaceTo: suggestion,
      autoApplicable: true,
    };
  }

  // ------------------------------------------------------------
  // Medication: present in source but missing from note (lost)
  // ------------------------------------------------------------
  if (
    issue.family === 'incomplete_medication_detail' &&
    attrs.kind === 'lost_in_note'
  ) {
    const drug = String(attrs.medication ?? '');
    return {
      kind: 'add_to_section',
      targetSide: 'note',
      targetSection: 'medications',
      headline: `Add "${drug}" to Medications`,
      rationale: `"${drug}" is mentioned in the summary but missing from the note's medications section.`,
      current: getSectionContent(note as unknown as Record<string, { content?: string }>, 'medications'),
      proposed:
        getSectionContent(note as unknown as Record<string, { content?: string }>, 'medications').trim() +
        (getSectionContent(note as unknown as Record<string, { content?: string }>, 'medications') ? '; ' : '') +
        drug,
      autoApplicable: true,
    };
  }

  // ------------------------------------------------------------
  // Medication: incomplete dosing (drug in note without dose+unit)
  // ------------------------------------------------------------
  if (
    issue.family === 'incomplete_medication_detail' &&
    attrs.kind === 'incomplete_dosing'
  ) {
    const drug = String(attrs.medication ?? '');
    return {
      kind: 'manual',
      targetSide: 'note',
      targetSection: section,
      headline: `Add dose + unit for "${drug}"`,
      rationale: `The note mentions "${drug}" without a dose or unit. Required for legal/billing documentation.`,
      autoApplicable: false,
    };
  }

  // ------------------------------------------------------------
  // Note drift: Modified (note rewrote summary section)
  // ------------------------------------------------------------
  if (
    issue.family === 'chart_conflict' &&
    attrs.noteDrift === 'modified' &&
    section
  ) {
    const summaryText = getSectionContent(
      summary as unknown as Record<string, { content?: string }>,
      section,
    );
    const noteText = getSectionContent(
      note as unknown as Record<string, { content?: string }>,
      section,
    );
    if (summaryText && noteText && summaryText !== noteText) {
      return {
        kind: 'set_section_content',
        targetSide: 'note',
        targetSection: section,
        headline: `Revert "${section}" to summary content`,
        rationale:
          'The note generator rewrote this section. Reverting restores the clinician-validated summary text.',
        current: noteText,
        proposed: summaryText,
        autoApplicable: true,
      };
    }
  }

  // ------------------------------------------------------------
  // Note drift: Invented (note has content the summary never did)
  // ------------------------------------------------------------
  if (
    issue.family === 'unsupported_assertion' &&
    (attrs.noteDrift === 'invented' || attrs.noteEvidence === 'empty') &&
    section
  ) {
    const noteText = getSectionContent(
      note as unknown as Record<string, { content?: string }>,
      section,
    );
    return {
      kind: 'clear_section',
      targetSide: 'note',
      targetSection: section,
      headline: `Clear "${section}" — no transcript backing`,
      rationale:
        attrs.noteEvidence === 'empty'
          ? 'The note generator emitted content for this section with empty evidenceSegments AND the summary has no content. Likely a hallucination.'
          : 'The summary never captured this section, but the note populated it. Removing prevents documenting unsourced content.',
      current: noteText,
      proposed: '',
      autoApplicable: true,
    };
  }

  // ------------------------------------------------------------
  // Numeric fabrication
  // ------------------------------------------------------------
  if (
    issue.family === 'unsupported_assertion' &&
    attrs.kind === 'numeric_fabrication'
  ) {
    return {
      kind: 'manual',
      targetSide: 'note',
      targetSection: section,
      headline: `Verify "${attrs.value} ${attrs.unit}" — not in transcript or summary`,
      rationale: `The note states "${attrs.value} ${attrs.unit}" but neither the transcript nor the summary mention this value. Confirm with the clinician before signing.`,
      autoApplicable: false,
    };
  }

  // ------------------------------------------------------------
  // Section relocation
  // ------------------------------------------------------------
  if (
    issue.family === 'wrong_section_assignment' &&
    attrs.noteDrift === 'relocated'
  ) {
    const from = String(attrs.fromSection ?? '');
    const to = String(attrs.toSection ?? '');
    return {
      kind: 'manual',
      targetSide: 'note',
      targetSection: from,
      headline: `Content moved from "${from}" to "${to}"`,
      rationale: `The note generator relocated content the summary placed in "${from}". Move tokens like ${(attrs.relocatedTokens as string[] | undefined)?.slice(0, 3).join(', ') ?? '—'} back to the original section.`,
      autoApplicable: false,
    };
  }

  // ============================================================
  // FALLBACK TEMPLATES — family-level catch-alls.
  //
  // Reached only when none of the specific (family + attribute)
  // patterns above matched. These don't auto-apply; they give the
  // auditor a structured "what does this issue mean and what do I do
  // about it?" block instead of falling through to nothing. As we
  // promote more attrs into specific patterns above, fewer signals
  // will hit these fallbacks.
  // ============================================================

  // Helper: pick the most informative evidence quote for the rationale.
  // Prefers transcript evidence (the source of truth) over summary or
  // chart, and skips empty / sentinel strings.
  const pickQuote = (
    sourceFilter?: EvidenceSpan['source'][],
  ): string | null => {
    const spans = issue.evidenceBundle ?? [];
    const filtered = sourceFilter
      ? spans.filter((s) => sourceFilter.includes(s.source))
      : spans;
    for (const s of filtered) {
      const t = (s.text ?? '').trim();
      if (t.length > 0) return t;
    }
    return null;
  };

  if (issue.family === 'incomplete_medication_detail') {
    // TAH detectors fire this for missing dose / frequency / route.
    // Without rxnormSuggestions or our kind tag, we can't auto-fix —
    // but we can pinpoint which medication the auditor needs to amend.
    const med = pickQuote();
    return {
      kind: 'manual',
      targetSide: 'note',
      targetSection: section ?? 'medications',
      headline: med
        ? `Add dose / frequency / route for "${med}"`
        : 'Add missing medication detail',
      rationale: med
        ? `TAH detected "${med}" in the transcript or summary but the note's medications entry is missing one of: dose, frequency, or route. Required for legal/billing documentation.`
        : 'TAH flagged a medication mention as missing required detail.',
      autoApplicable: false,
    };
  }

  if (issue.family === 'unsupported_assertion') {
    const claim = pickQuote(['summary', 'chart']);
    return {
      kind: 'manual',
      targetSide: 'note',
      targetSection: section,
      headline: claim
        ? `Verify "${claim.length > 60 ? claim.slice(0, 60) + '…' : claim}" against transcript`
        : 'Verify assertion against transcript',
      rationale: claim
        ? `The LLM judge could not find transcript evidence backing the claim "${claim}". Confirm against the recording — if unsupported, edit or remove from the note.`
        : 'The LLM judge could not find transcript evidence for this assertion. Confirm against the recording before signing.',
      autoApplicable: false,
    };
  }

  if (issue.family === 'missing_clinical_fact') {
    const fact = pickQuote(['transcript']);
    return {
      kind: 'manual',
      targetSide: 'note',
      targetSection: section,
      headline: fact
        ? `Add "${fact.length > 60 ? fact.slice(0, 60) + '…' : fact}" to the note`
        : 'Add missing clinical fact to the note',
      rationale: fact
        ? `The transcript mentions "${fact}" but the note doesn't reflect it${section ? ` in ${section}` : ''}. Decide whether to incorporate.`
        : 'The LLM judge flagged a clinical fact present in the transcript that did not survive into the note.',
      autoApplicable: false,
    };
  }

  if (issue.family === 'wrong_section_assignment') {
    const content = pickQuote();
    return {
      kind: 'manual',
      targetSide: 'note',
      targetSection: section,
      headline: content
        ? `Re-classify "${content.length > 60 ? content.slice(0, 60) + '…' : content}"`
        : 'Re-classify section content',
      rationale:
        'The LLM judge thinks this content belongs in a different section. Review the evidence and move it if appropriate.',
      autoApplicable: false,
    };
  }

  if (issue.family === 'chart_conflict') {
    const conflict = pickQuote();
    return {
      kind: 'manual',
      targetSide: 'note',
      targetSection: section,
      headline: conflict
        ? `Reconcile conflict on "${conflict.length > 60 ? conflict.slice(0, 60) + '…' : conflict}"`
        : 'Reconcile chart conflict',
      rationale:
        'Summary and note disagree on a clinical fact. Compare evidence side-by-side and pick the version supported by the transcript.',
      autoApplicable: false,
    };
  }

  if (issue.family === 'procedure_capture_gap') {
    const proc = pickQuote(['transcript']);
    return {
      kind: 'manual',
      targetSide: 'note',
      targetSection: section ?? 'treatment',
      headline: proc
        ? `Document procedure: "${proc.length > 60 ? proc.slice(0, 60) + '…' : proc}"`
        : 'Document procedure performed',
      rationale: proc
        ? `TAH detected the procedure "${proc}" in the transcript. Verify the note captures it with all required details.`
        : 'TAH detected a procedure mentioned in the transcript that the note may not have captured.',
      autoApplicable: false,
    };
  }

  if (issue.family === 'speaker_role_inconsistency') {
    const utterance = pickQuote(['transcript']);
    return {
      kind: 'manual',
      targetSide: 'note',
      targetSection: null,
      headline: utterance
        ? `Verify speaker for "${utterance.length > 60 ? utterance.slice(0, 60) + '…' : utterance}"`
        : 'Verify speaker attribution',
      rationale:
        'A statement may be attributed to the wrong speaker (physician vs patient). Confirm against the transcript.',
      autoApplicable: false,
    };
  }

  if (issue.family === 'followup_or_plan_gap') {
    const plan = pickQuote(['transcript']);
    return {
      kind: 'manual',
      targetSide: 'note',
      targetSection: 'plan',
      headline: plan
        ? `Add follow-up: "${plan.length > 60 ? plan.slice(0, 60) + '…' : plan}"`
        : 'Document follow-up plan',
      rationale: plan
        ? `TAH detected "${plan}" in the transcript suggesting a follow-up element. Add it to the plan section if clinically appropriate.`
        : 'TAH expected a follow-up / plan element that is missing from the note.',
      autoApplicable: false,
    };
  }

  if (issue.family === 'enrichment_suggestion') {
    const suggestion = pickQuote();
    return {
      kind: 'manual',
      targetSide: 'note',
      targetSection: section,
      headline: suggestion
        ? `Consider adding: "${suggestion.length > 60 ? suggestion.slice(0, 60) + '…' : suggestion}"`
        : 'Optional enrichment',
      rationale:
        'This is a non-gating suggestion to improve note quality. Consider incorporating if the addition is supported by evidence.',
      autoApplicable: false,
    };
  }

  return null;
}
